import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  BookOpen, Wind, Heart, LogOut, Menu, X, 
  Settings, Crown, Home, Activity,
  Clock, AlertCircle, Wallet, Users, 
  Calendar, Dumbbell, ChevronRight, BarChart3, Search, Mic, Trophy, Flame, TrendingUp, Star, StarOff, Gift
} from 'lucide-react';
import { useState, useEffect } from 'react';
import Disclaimer from '@/components/Disclaimer';
import Onboarding from '@/components/Onboarding';
import Footer from '@/components/Footer';
import GlobalSearch from '@/components/GlobalSearch';
import NotificationPrompt from '@/components/NotificationPrompt';
import { useDailyReminder } from '@/hooks/useDailyReminder';
import { useScrollRestoration } from '@/hooks/useScrollRestoration';
import axios from 'axios';

const LIVON_LOGO = "https://static.prod-images.emergentagent.com/jobs/b293bd06-5db6-47e3-9bac-b35b794055ae/images/24e9f4cae61d982d9c454efb36258018ae47a7c7ef1d43817c3543666c110b4a.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Main categories
const CATEGORIES = [
  { 
    id: 'thoughts', 
    title: 'Tankar & känslor', 
    emoji: '◐',
    subtitle: 'Dagbok, reflektioner & humör',
    icon: Heart,
    path: '/category/thoughts',
    gradient: 'from-blue-50 to-indigo-50',
    borderColor: 'border-blue-200',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
  },
  { 
    id: 'health', 
    title: 'Hälsa & kropp', 
    emoji: '❋',
    subtitle: 'Träning, kost & sömn',
    icon: Dumbbell,
    path: '/category/health',
    gradient: 'from-emerald-50 to-teal-50',
    borderColor: 'border-emerald-200',
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
  },
  { 
    id: 'calm', 
    title: 'Lugn & fokus', 
    emoji: '◎',
    subtitle: 'Andning, meditation & fokus',
    icon: Wind,
    path: '/category/calm',
    gradient: 'from-violet-50 to-purple-50',
    borderColor: 'border-violet-200',
    iconBg: 'bg-violet-100',
    iconColor: 'text-violet-600',
  },
  { 
    id: 'home', 
    title: 'Mitt hem', 
    emoji: '⌂',
    subtitle: 'Städ, rutiner & projekt',
    icon: Home,
    path: '/category/home',
    gradient: 'from-amber-50 to-orange-50',
    borderColor: 'border-amber-200',
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
  },
  { 
    id: 'planning', 
    title: 'Planering', 
    emoji: '▦',
    subtitle: 'Mål, vanor & kalender',
    icon: Calendar,
    path: '/category/planning',
    gradient: 'from-sky-50 to-cyan-50',
    borderColor: 'border-sky-200',
    iconBg: 'bg-sky-100',
    iconColor: 'text-sky-600',
  },
  { 
    id: 'economy', 
    title: 'Ekonomi & karriär', 
    emoji: '◈',
    subtitle: 'Budget, sparande & jobb',
    icon: Wallet,
    path: '/category/economy',
    gradient: 'from-teal-50 to-emerald-50',
    borderColor: 'border-teal-200',
    iconBg: 'bg-teal-100',
    iconColor: 'text-teal-600',
  },
  { 
    id: 'relations', 
    title: 'Relationer', 
    emoji: '∞',
    subtitle: 'Kommunikation & kärlek',
    icon: Users,
    path: '/category/relations',
    gradient: 'from-rose-50 to-pink-50',
    borderColor: 'border-rose-200',
    iconBg: 'bg-rose-100',
    iconColor: 'text-rose-600',
  },
];

// All available tools for favorites
const ALL_QUICK_TOOLS = [
  { id: 'journal', label: 'Dagbok', icon: BookOpen, path: '/journal' },
  { id: 'voice', label: 'Röstinspelning', icon: Mic, path: '/voice' },
  { id: 'mood', label: 'Humör', icon: Activity, path: '/mood' },
  { id: 'exercises', label: 'Andning', icon: Wind, path: '/exercises' },
  { id: 'planner', label: 'Veckoplanering', icon: Calendar, path: '/week-planner' },
  { id: 'stats', label: 'Statistik', icon: BarChart3, path: '/stats' },
  { id: 'fitness', label: 'Träning', icon: Dumbbell, path: '/fitness' },
  { id: 'goals', label: 'Mål', icon: Trophy, path: '/goals' },
  { id: 'habits', label: 'Vanor', icon: Heart, path: '/habits' },
  { id: 'notes', label: 'Anteckningar', icon: BookOpen, path: '/notes' },
  { id: 'finance', label: 'Ekonomi', icon: Wallet, path: '/finance' },
];

export default function DashboardPage() {
  const { user, logout, isPremium, isTrial, trialDaysLeft, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [streak, setStreak] = useState({ current_streak: 0, longest_streak: 0 });
  const [favorites, setFavorites] = useState([]);
  // Restore scroll position when returning to dashboard
  useScrollRestoration('dashboard');
  useDailyReminder();

  useEffect(() => {
    const hasSeenOnboarding = localStorage.getItem('onboarding-completed');
    if (!hasSeenOnboarding) {
      setShowOnboarding(true);
    }
    
    // Load favorites
    const loadFavorites = async () => {
      try {
        const res = await axios.get(`${API}/dashboard/favorites`, { headers: getAuthHeader() });
        setFavorites(res.data.favorites || []);
      } catch (e) {}
    };
    loadFavorites();
    
    // Keyboard shortcut for search (Cmd/Ctrl + K)
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowSearch(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (showOnboarding) {
    return <Onboarding onComplete={() => { setShowOnboarding(false); }} />;
  }

  return (
    <div className="min-h-screen bg-white" data-testid="dashboard-page">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-4 py-3 flex justify-between items-center">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/dashboard')}>
            <img src={LIVON_LOGO} alt="Livon" className="h-9 w-auto" />
          </div>
          
          {/* Search button */}
          <button
            onClick={() => setShowSearch(true)}
            className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted hover:bg-muted/80 transition-colors"
          >
            <Search className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Sök...</span>
          </button>
          
          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-3">
            {isTrial && !isPremium && (
              <div className="flex items-center gap-2 text-sm px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 font-medium">
                <Clock className="w-4 h-4" />
                {trialDaysLeft} dagar kvar
              </div>
            )}
            {isPremium && (
              <span className="text-sm px-3 py-1.5 rounded-full bg-primary/10 text-primary flex items-center gap-2 font-medium">
                <Crown className="w-4 h-4" />
                Premium
              </span>
            )}
            <span className="text-sm text-foreground font-medium">{user?.name}</span>
            <Button variant="ghost" onClick={() => navigate('/settings')} className="rounded-full w-9 h-9 p-0" data-testid="settings-btn">
              <Settings className="w-5 h-5" />
            </Button>
            <Button variant="ghost" onClick={handleLogout} className="rounded-full w-9 h-9 p-0" data-testid="logout-btn">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>

          {/* Mobile menu */}
          <Button variant="ghost" className="md:hidden rounded-full w-9 h-9 p-0" onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-btn">
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>

        {menuOpen && (
          <div className="md:hidden border-t border-border/30 bg-white p-4 space-y-2">
            <p className="text-sm text-foreground px-3 font-medium">Hej, {user?.name}</p>
            {isTrial && !isPremium && (
              <div className="flex items-center gap-2 text-sm px-3 py-1.5 text-blue-700 font-medium">
                <Clock className="w-4 h-4" />
                {trialDaysLeft} dagar kvar av provperioden
              </div>
            )}
            <Button variant="ghost" onClick={() => { navigate('/settings'); setMenuOpen(false); }} className="w-full justify-start rounded-xl">
              <Settings className="w-4 h-4 mr-2" /> Inställningar
            </Button>
            <Button variant="ghost" onClick={handleLogout} className="w-full justify-start rounded-xl">
              <LogOut className="w-4 h-4 mr-2" /> Logga ut
            </Button>
          </div>
        )}
      </header>

      <NotificationPrompt />

      {/* Trial Warning Banner - Shows when trial is ending soon or expired */}
      {!isPremium && isTrial && trialDaysLeft <= 3 && (
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-3">
          <div className="max-w-2xl mx-auto flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm font-medium">
                {trialDaysLeft === 1 
                  ? 'Sista dagen av din provperiod!' 
                  : `Endast ${trialDaysLeft} dagar kvar av din provperiod`}
              </span>
            </div>
            <Button 
              size="sm" 
              onClick={() => navigate('/subscription')}
              className="bg-white text-orange-600 hover:bg-orange-50 font-semibold text-xs px-3 py-1 h-auto"
            >
              Uppgradera nu
            </Button>
          </div>
        </div>
      )}

      {/* Trial Expired Banner */}
      {!isPremium && !isTrial && user?.trial_end && (
        <div className="bg-gradient-to-r from-red-500 to-rose-500 text-white px-4 py-3">
          <div className="max-w-2xl mx-auto flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm font-medium">
                Din provperiod har gått ut. Uppgradera för att fortsätta använda alla funktioner.
              </span>
            </div>
            <Button 
              size="sm" 
              onClick={() => navigate('/subscription')}
              className="bg-white text-red-600 hover:bg-red-50 font-semibold text-xs px-3 py-1 h-auto"
            >
              Uppgradera
            </Button>
          </div>
        </div>
      )}

      <main className="max-w-2xl mx-auto px-4 py-6">
        {/* Welcome */}
        <div className="mb-6">
          <h1 className="font-heading text-2xl font-bold text-foreground">
            Hej, {user?.name}
          </h1>
          <p className="text-foreground/60 mt-1">Vad vill du arbeta med idag?</p>
        </div>

        {/* Category Cards - 2 column grid */}
        <div className="grid grid-cols-2 gap-3">
          {CATEGORIES.map((category, index) => {
            const isLast = index === CATEGORIES.length - 1 && CATEGORIES.length % 2 !== 0;
            return (
            <button
              key={category.id}
              onClick={() => navigate(category.path)}
              className={`bg-gradient-to-br ${category.gradient} p-4 rounded-2xl text-left group border ${category.borderColor} hover:shadow-lg transition-all duration-200 ${isLast ? 'col-span-2 max-w-[calc(50%-6px)] mx-auto' : ''}`}
              data-testid={`category-${category.id}`}
            >
              <div className={`w-10 h-10 rounded-xl ${category.iconBg} flex items-center justify-center mb-3`}>
                <category.icon className={`w-5 h-5 ${category.iconColor}`} />
              </div>
              <h2 className="font-heading font-bold text-[16px] leading-tight text-foreground group-hover:text-primary transition-colors">
                {category.title}
              </h2>
              <p className="text-[13px] text-foreground/55 mt-1 leading-snug">
                {category.subtitle}
              </p>
            </button>
            );
          })}
        </div>

        {/* Weekly Summary */}
        <div className="mt-6">
          <button
            onClick={() => navigate('/weekly-summary')}
            className="w-full flex items-center gap-4 p-4 rounded-2xl bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-200 hover:shadow-lg transition-all group"
            data-testid="weekly-summary-btn"
          >
            <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-indigo-600" />
            </div>
            <div className="text-left flex-1">
              <p className="text-[16px] font-bold text-foreground group-hover:text-primary transition-colors">Veckorapport</p>
              <p className="text-[13px] text-foreground/55">Se din aktivitet denna vecka</p>
            </div>
            <ChevronRight className="w-5 h-5 text-indigo-400 group-hover:text-primary transition-colors" />
          </button>
        </div>

        {/* Favorites Section */}
        {favorites.length > 0 && (
          <div className="mt-6">
            <h3 className="text-[13px] font-semibold text-foreground/40 uppercase tracking-wider mb-3 px-1">Dina favoriter</h3>
            <div className="flex flex-wrap gap-2">
              {favorites.map((fav, i) => {
                const match = ALL_QUICK_TOOLS.find(t => t.id === fav);
                if (!match) return null;
                return (
                  <button
                    key={fav}
                    onClick={() => navigate(match.path)}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-slate-100 hover:bg-primary/10 hover:text-primary transition-all border border-slate-200"
                    data-testid={`favorite-${fav}`}
                  >
                    <match.icon className="w-4 h-4" />
                    <span className="text-sm font-medium">{match.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 text-center space-y-4">
          <Disclaimer variant="mini" />
          <Button
            variant="ghost"
            onClick={() => navigate('/crisis')}
            className="text-muted-foreground hover:text-foreground text-sm"
            data-testid="crisis-btn"
          >
            <AlertCircle className="w-4 h-4 mr-2" />
            Behöver du prata med någon?
          </Button>
        </div>
      </main>

      <Footer />
      
      {/* Global Search Modal */}
      <GlobalSearch isOpen={showSearch} onClose={() => setShowSearch(false)} />
    </div>
  );
}
